import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import Confetti from 'react-confetti';
import '../styles/Raffle.css';

const Raffle = () => {
    const [isRaffleActive, setIsRaffleActive] = useState(false);
    const [challenges, setChallenges] = useState([]);
    const [error, setError] = useState(null);
    const [isCompleting, setIsCompleting] = useState(false);
    const [newChallengeIds, setNewChallengeIds] = useState(new Set());
    const [showConfetti, setShowConfetti] = useState(false);
    const userName = Cookies.get('bbq_user_name');
    
    // Store IDs we've already processed to prevent repeated animations
    const processedChallengeIds = useRef(new Set());
    const isFirstFetch = useRef(true);
    
    // Fetch user's challenges and determine raffle status
    useEffect(() => {
        const fetchChallenges = async () => {
            try {
                if (userName) {
                    const challengesResponse = await fetch(`/api/raffle/challenges/${userName}`);
                    if (challengesResponse.ok) {
                        const challengesData = await challengesResponse.json();
                        const newChallenges = challengesData.challenges;
                        
                        // Skip animation on first fetch
                        if (!isFirstFetch.current) {
                            // Find truly new challenges (ones we haven't processed before)
                            const trulyNewChallenges = newChallenges.filter(
                                challenge => !processedChallengeIds.current.has(challenge.challenge_id)
                            );
                            
                            if (trulyNewChallenges.length > 0) {
                                console.log("New challenges detected:", trulyNewChallenges.map(c => c.challenge_id));
                                
                                // Add these to our processed set so we don't animate them again
                                trulyNewChallenges.forEach(challenge => {
                                    processedChallengeIds.current.add(challenge.challenge_id);
                                });
                                
                                // Set the new challenge IDs for animation
                                const newIds = new Set(trulyNewChallenges.map(c => c.challenge_id));
                                setNewChallengeIds(newIds);
                                
                                // Show confetti
                                setShowConfetti(true);
                                
                                // Remove confetti after animation
                                setTimeout(() => {
                                    setShowConfetti(false);
                                }, 3000);
                                
                                // Remove the new-challenge highlight after a bit longer
                                setTimeout(() => {
                                    setNewChallengeIds(new Set());
                                }, 5000);
                            }
                        } else {
                            // On first fetch, add all challenge IDs to processed set
                            newChallenges.forEach(challenge => {
                                processedChallengeIds.current.add(challenge.challenge_id);
                            });
                            isFirstFetch.current = false;
                        }
                        
                        // Update challenges state
                        setChallenges(newChallenges);
                        setIsRaffleActive(newChallenges.length > 0);
                    } else {
                        const errorData = await challengesResponse.json();
                        setError(errorData.error || 'Failed to fetch challenges');
                        setIsRaffleActive(false);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch challenges:', err);
                setError('Failed to fetch challenges');
                setIsRaffleActive(false);
            }
        };

        // Initial fetch
        fetchChallenges();
        
        // Poll for updates every 5 seconds
        const interval = setInterval(fetchChallenges, 5000);

        return () => clearInterval(interval);
    }, [userName]);

    const handleCompleteChallenge = async (challengeId) => {
        setIsCompleting(true);
        setError(null);

        try {
            const response = await fetch(`/api/raffle/challenges/${challengeId}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ memberName: userName })
            });

            if (response.ok) {
                // Update the challenge status locally
                setChallenges(challenges.map(challenge =>
                    challenge.challenge_id === challengeId
                        ? { ...challenge, is_completed: true }
                        : challenge
                ));
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to complete challenge');
            }
        } catch (err) {
            console.error('Failed to complete challenge:', err);
            setError('Failed to complete challenge');
        } finally {
            setIsCompleting(false);
        }
    };

    return (
        <div className="raffle-user-view">
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    numberOfPieces={500}
                    recycle={false}
                    gravity={0.3}
                    tweenDuration={500}
                    initialVelocityX={15}
                    initialVelocityY={15}
                    colors={['#ff7700', '#ff9d4d', '#ffb74d', '#f48fb1', '#64b5f6', '#ffeb3b', '#ff5722']}
                />
            )}
            <h3>BBQ Challenges</h3>

            {error && <p className="error-message">{error}</p>}

            {isRaffleActive ? (
                challenges.length > 0 ? (
                    <div className="challenges-list">
                        {challenges.map((challenge) => (
                            <div
                                key={challenge.challenge_id}
                                className={`challenge-card ${challenge.is_completed ? 'completed' : ''} ${
                                    newChallengeIds.has(challenge.challenge_id) ? 'new-challenge' : ''
                                }`}
                            >
                                <div className="challenge-header">
                                    <h4>Challenge #{challenge.challenge_id}</h4>
                                    <span className={`difficulty-badge difficulty-${challenge.difficulty}`}>
                                        Level {challenge.difficulty}
                                    </span>
                                </div>
                                <p className="challenge-text">{challenge.task_text}</p>
                                {challenge.participants && challenge.participants.length > 0 && (
                                    <p className="partner-info">
                                        Team up with: <span>{challenge.participants.join(', ')}</span>
                                    </p>
                                )}
                                <div className="challenge-footer">
                                    <span className="challenge-date">
                                        {new Date(challenge.created_at).toLocaleDateString()}
                                    </span>
                                    {!challenge.is_completed && (
                                        <button
                                            className="complete-button"
                                            onClick={() => handleCompleteChallenge(challenge.challenge_id)}
                                            disabled={isCompleting}
                                        >
                                            {isCompleting ? 'Completing...' : 'Mark as Completed'}
                                        </button>
                                    )}
                                    {challenge.is_completed && (
                                        <span className="completed-badge">Completed!</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="waiting-message">
                        The raffle is active, but you haven't been assigned any challenges yet. Check back soon!
                    </p>
                )
            ) : (
                <p className="waiting-message">
                    No active raffle at the moment. Wait for the admin to start one!
                </p>
            )}
        </div>
    );
};

export default Raffle; 